import { createStore } from 'vuex';
import api from '../api/webportal'; // Make sure this path is correct
import createPersistedState from 'vuex-persistedstate';

const store = createStore({
  plugins: [
    createPersistedState({
      paths: ['user', 'loggedIn', 'token'], // These parts of the state will be persisted
    }),
  ],
  state: {
    user: null,
    token: null,
    loggedIn: false,
    authReady: false, // Indicates if initial auth check is complete
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
      state.loggedIn = !!user; // Set loggedIn to true if user object exists, false otherwise
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_AUTH_READY(state, status) {
      state.authReady = status;
    },
  },
  actions: {
    // This action fetches a CSRF token. It's primarily for session-based auth,
    // but can be kept for consistency or if you use other cookie-based features.
    async getCsrfToken() {
      try {
        await api.get('/sanctum/csrf-cookie');
      } catch (error) {
        console.error('Failed to get CSRF token:', error);
      }
    },

    async login({ commit, dispatch }, credentials) {
      // Dispatch getCsrfToken only if you strictly need it for session (e.g., initial login page)
      await dispatch('getCsrfToken'); // Generally not needed for pure token-based auth after initial token acquisition

      try {
        // Post credentials (username, password, device_name) to your Laravel login endpoint
        const response = await api.post('/api/login', credentials);
        const { user, token } = response.data; // Destructure user and token from response

        commit('SET_USER', user);
        commit('SET_TOKEN', token);

        // Set default Authorization header for ALL subsequent requests via this Axios instance
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        return response.data; // Return data for component to handle success
      } catch (error) {
        // Clear user/token on login failure
        commit('SET_USER', null);
        commit('SET_TOKEN', null);
        delete api.defaults.headers.common['Authorization']; // Ensure header is removed
        throw error; // Re-throw error for component to handle
      }
    },

    // This action would be for user registration, if you implement it
    async register({ dispatch }, userData) {
      await dispatch('getCsrfToken'); // Optional for token auth
      try {
        const response = await api.post('/api/register', userData); // Assuming '/api/register' endpoint
        return response.data;
      } catch (error) {
        console.error('Registration error:', error);
        throw error;
      }
    },

    // Fetches the authenticated user's data from the backend
    async fetchUser({ commit, state }) {
      console.log('Attempting to fetch user...');
      if (!state.token) {
        console.warn('No token found in state. Skipping user fetch.');
        commit('SET_USER', null); // Ensure user is null if no token
        commit('SET_AUTH_READY', true); // Mark auth as ready even if not logged in
        return;
      }

      // Set the Authorization header for this request (Axios interceptors might also do this)
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;

      try {
        const response = await api.get('/api/user'); // Endpoint to get current authenticated user
        console.log('User fetched successfully:', response.data);
        commit('SET_USER', response.data); // Commit the fetched user data
      } catch (error) {
        console.error('Failed to fetch user:', error.response?.data || error.message);
        // If 401 Unauthorized, token might be expired or invalid; log out
        if (error.response && error.response.status === 401) {
          console.warn('User session expired or invalid token. Logging out.');
          commit('SET_USER', null);
          commit('SET_TOKEN', null);
          delete api.defaults.headers.common['Authorization']; // Clear header
        }
      } finally {
        commit('SET_AUTH_READY', true); // Mark auth status as checked
      }
    },

    // Logs out the user by revoking the token on the backend
    async logout({ commit, state }) {
      if (!state.token) return; // No token, nothing to logout

      // Set token header for the logout request
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;

      try {
        await api.post('/api/logout'); // Endpoint to logout (revoke token)
        console.log('Logged out successfully on backend.');
      } catch (error) {
        console.error('Failed to logout on backend:', error.response?.data || error.message);
        // Even if backend logout fails, clear local state for user experience
      } finally {
        // Always clear local state regardless of backend success/failure
        commit('SET_USER', null);
        commit('SET_TOKEN', null);
        delete api.defaults.headers.common['Authorization']; // Remove auth header
      }
    },
  },
  getters: {
    isAuthenticated: (state) => state.loggedIn,
    currentUser: (state) => state.user,
    isAuthReady: (state) => state.authReady,
    // Adjusting getters based on AuthController returning role as a direct string
    userRole: (state) => state.user?.role || null, // Access `role` directly as it's a string
    isAdmin: (state) => state.user?.role === 'admin', // Compare `role` string directly
    // Add other roles as needed
    isManager: (state) => state.user?.role === 'manager',
    isUser: (state) => state.user?.role === 'user',
  },
});

export default store;
